import { useState } from 'react';

import { adminUpdateClubStatusApi, getClubApi, updateClubApi } from '@/lib/api/clubsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr, getNameInitials } from '@shared';
import { ClubPlan } from '@shared/enums/domain.enum';
import type { Club } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Crown, ExternalLink, Loader2, MapPin, Power, X } from 'lucide-react';
import { toast } from 'sonner';

import CmsOfferControl from './CmsOfferControl';

interface ClubDetailDrawerProps {
  clubId: number | null;
  onClose: () => void;
  frontUrl: string;
}

type TabKey = 'infos' | 'edit' | 'admin';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'infos', label: 'Infos' },
  { key: 'edit', label: 'Édition' },
  { key: 'admin', label: 'Admin' },
];

const FIELD_CLS =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none';

interface EditForm {
  name: string;
  email: string;
  phone: string;
  description: string;
  address: string;
  city: string;
  postcode: string;
  department: string;
  region: string;
  registrationNumber: string;
  siren: string;
  isProfilePublic: boolean;
}

function toEditForm(c: Club): EditForm {
  return {
    name: c.name ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    description: c.description ?? '',
    address: c.address ?? '',
    city: c.city ?? '',
    postcode: c.postcode ?? '',
    department: c.department ?? '',
    region: c.region ?? '',
    registrationNumber: c.registrationNumber ?? '',
    siren: c.siren ?? '',
    isProfilePublic: c.isProfilePublic,
  };
}

export default function ClubDetailDrawer({ clubId, onClose, frontUrl }: ClubDetailDrawerProps) {
  const [tab, setTab] = useState<TabKey>('infos');

  // Reset to first tab when a different club is opened (store-previous-prop pattern).
  const [prevClubId, setPrevClubId] = useState<number | null>(null);
  if (clubId !== null && clubId !== prevClubId) {
    setPrevClubId(clubId);
    setTab('infos');
  }

  const open = clubId !== null;

  const { data: club, isLoading } = useQuery({
    queryKey: queryKeys.clubs.detail(clubId ?? 0),
    queryFn: () => getClubApi(clubId as number),
    enabled: open,
  });

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />
      <aside
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className='flex items-start gap-3 border-b border-slate-200 p-5'>
          {club && (
            <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-sm font-bold text-white'>
              {getNameInitials(club.name)}
            </div>
          )}
          <div className='min-w-0 flex-1'>
            <h2 className='truncate text-lg font-bold text-slate-900'>{club?.name ?? 'Chargement…'}</h2>
            {club && <p className='truncate font-mono text-xs text-slate-400'>#{club.alias}</p>}
          </div>
          <a
            href={`${frontUrl}/clubs/${clubId}`}
            target='_blank'
            rel='noreferrer'
            className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-blue-600'
            title='Voir le profil public'
          >
            <ExternalLink className='h-4 w-4' />
          </a>
          <button
            onClick={onClose}
            className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100'
            aria-label='Fermer'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Tabs */}
        <div className='flex gap-1 border-b border-slate-200 px-5'>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-semibold transition ${
                tab === t.key
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-5'>
          {isLoading || !club ? (
            <div className='flex h-40 items-center justify-center text-slate-400'>
              <Loader2 className='h-6 w-6 animate-spin' />
            </div>
          ) : (
            <>
              {tab === 'infos' && <InfosTab club={club} />}
              {tab === 'edit' && <EditTab club={club} />}
              {tab === 'admin' && <AdminTab club={club} />}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

// ─── Infos ─────────────────────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='flex items-center justify-between gap-3 py-2'>
      <span className='text-sm text-slate-500'>{label}</span>
      <span className='text-right text-sm font-semibold text-slate-900'>{children}</span>
    </div>
  );
}

function InfosTab({ club }: { club: Club }) {
  return (
    <div className='space-y-6'>
      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-2 text-sm font-bold text-slate-900'>Statut</h3>
        <InfoRow label='Actif'>
          {club.isActive ? (
            <span className='rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'>Actif</span>
          ) : (
            <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500'>Inactif</span>
          )}
        </InfoRow>
        <InfoRow label='Plan'>
          {club.clubPlan === ClubPlan.PRO ? (
            <span className='inline-flex items-center gap-1 text-amber-700'>
              <Crown className='h-3.5 w-3.5' /> Pro
            </span>
          ) : (
            <span className='text-slate-600'>Gratuit</span>
          )}
        </InfoRow>
        <InfoRow label='Profil public'>{club.isProfilePublic ? 'Oui' : 'Non'}</InfoRow>
      </div>

      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-2 text-sm font-bold text-slate-900'>Coordonnées</h3>
        <InfoRow label='Email'>{club.email ?? '—'}</InfoRow>
        <InfoRow label='Téléphone'>{club.phone ?? '—'}</InfoRow>
        <InfoRow label='Localisation'>
          <span className='inline-flex items-center gap-1'>
            <MapPin className='h-3.5 w-3.5 text-slate-400' />
            {[club.city, club.department].filter(Boolean).join(', ') || '—'}
          </span>
        </InfoRow>
        <InfoRow label='SIREN'>{club.siren ?? '—'}</InfoRow>
        <InfoRow label='N° RNA'>{club.registrationNumber ?? '—'}</InfoRow>
        <InfoRow label='Inscrit le'>{formatDateAbbreviatedFr(club.createdAt)}</InfoRow>
      </div>
    </div>
  );
}

// ─── Édition ───────────────────────────────────────────────────────

function EditTab({ club }: { club: Club }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<EditForm>(() => toEditForm(club));

  const mutation = useMutation({
    mutationFn: (payload: EditForm) =>
      updateClubApi(club.id, {
        name: payload.name.trim(),
        email: payload.email.trim() || undefined,
        phone: payload.phone.trim() || undefined,
        description: payload.description.trim() || undefined,
        address: payload.address.trim() || undefined,
        city: payload.city.trim() || undefined,
        postcode: payload.postcode.trim() || undefined,
        department: payload.department.trim() || undefined,
        region: payload.region.trim() || undefined,
        registrationNumber: payload.registrationNumber.trim() || undefined,
        siren: payload.siren.trim() || undefined,
        isProfilePublic: payload.isProfilePublic,
      }),
    onSuccess: () => {
      toast.success('Club mis à jour');
      void qc.invalidateQueries({ queryKey: queryKeys.clubs.detail(club.id) });
      void qc.invalidateQueries({ queryKey: queryKeys.clubs.all() });
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors de la mise à jour'),
  });

  function set<K extends keyof EditForm>(key: K, value: EditForm[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function field(label: string, key: keyof EditForm, type = 'text') {
    return (
      <div>
        <label className='mb-1 block text-xs font-semibold text-slate-600'>{label}</label>
        <input
          type={type}
          value={form[key] as string}
          onChange={e => set(key, e.target.value as EditForm[typeof key])}
          className={FIELD_CLS}
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!form.name.trim()) {
          toast.error('Le nom est requis');
          return;
        }
        mutation.mutate(form);
      }}
      className='space-y-4'
    >
      {!club.isActive && (
        <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
          <span>
            Ce club est inactif : l'édition du profil est bloquée tant qu'il n'est pas réactivé (onglet Admin).
          </span>
        </div>
      )}
      {field('Nom', 'name')}
      <div className='grid grid-cols-2 gap-3'>
        {field('Email', 'email', 'email')}
        {field('Téléphone', 'phone')}
      </div>
      {field('Adresse', 'address')}
      <div className='grid grid-cols-2 gap-3'>
        {field('Ville', 'city')}
        {field('Code postal', 'postcode')}
      </div>
      <div className='grid grid-cols-2 gap-3'>
        {field('Département', 'department')}
        {field('Région', 'region')}
      </div>
      <div className='grid grid-cols-2 gap-3'>
        {field('SIREN', 'siren')}
        {field('N° RNA', 'registrationNumber')}
      </div>

      <label className='flex items-center gap-2 text-sm text-slate-700'>
        <input
          type='checkbox'
          checked={form.isProfilePublic}
          onChange={e => set('isProfilePublic', e.target.checked)}
          className='h-4 w-4 rounded border-slate-300'
        />
        Profil public visible
      </label>

      <button
        type='submit'
        disabled={mutation.isPending}
        className='w-full rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:opacity-60'
      >
        {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </form>
  );
}

// ─── Admin (statut / plan) ─────────────────────────────────────────

function AdminTab({ club }: { club: Club }) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: { isActive?: boolean; clubPlan?: ClubPlan }) => adminUpdateClubStatusApi(club.id, payload),
    onSuccess: () => {
      toast.success('Statut du club mis à jour');
      void qc.invalidateQueries({ queryKey: queryKeys.clubs.detail(club.id) });
      void qc.invalidateQueries({ queryKey: queryKeys.clubs.all() });
      void qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors de la mise à jour'),
  });

  return (
    <div className='space-y-5'>
      <CmsOfferControl ownerType='CLUB' ownerId={club.id} ownerName={club.name} enabled={club.offerCms ?? false} />
      {/* Activation toggle */}
      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-1 flex items-center gap-2 text-sm font-bold text-slate-900'>
          <Power className='h-4 w-4 text-slate-400' /> Activation
        </h3>
        <p className='mb-3 text-xs text-slate-500'>
          Un club inactif est masqué et ses gestionnaires ne peuvent rien modifier.
        </p>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => mutation.mutate({ isActive: true })}
            disabled={mutation.isPending || club.isActive}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:opacity-50 ${
              club.isActive
                ? 'bg-emerald-100 text-emerald-700'
                : 'border border-emerald-200 text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            {club.isActive ? 'Actif' : 'Activer'}
          </button>
          <button
            onClick={() => mutation.mutate({ isActive: false })}
            disabled={mutation.isPending || !club.isActive}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:opacity-50 ${
              !club.isActive
                ? 'bg-slate-200 text-slate-600'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {!club.isActive ? 'Inactif' : 'Désactiver'}
          </button>
        </div>
      </div>

      {/* Plan */}
      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-1 flex items-center gap-2 text-sm font-bold text-slate-900'>
          <Crown className='h-4 w-4 text-slate-400' /> Plan
        </h3>
        <p className='mb-3 text-xs text-slate-500'>Forcer le plan du club (override administratif).</p>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => mutation.mutate({ clubPlan: ClubPlan.FREE })}
            disabled={mutation.isPending || club.clubPlan === ClubPlan.FREE}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:opacity-50 ${
              club.clubPlan === ClubPlan.FREE
                ? 'bg-slate-200 text-slate-700'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Gratuit
          </button>
          <button
            onClick={() => mutation.mutate({ clubPlan: ClubPlan.PRO })}
            disabled={mutation.isPending || club.clubPlan === ClubPlan.PRO}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:opacity-50 ${
              club.clubPlan === ClubPlan.PRO
                ? 'bg-amber-100 text-amber-700'
                : 'border border-amber-200 text-amber-700 hover:bg-amber-50'
            }`}
          >
            Pro
          </button>
        </div>
      </div>
    </div>
  );
}
